import React, { useMemo } from "react";

import { useFps } from "@/contexts/FpsContext";
import {
    InferenceStatus,
    useGetInferenceQueueItemById,
    usePredictionsForFileAndFrameSegment
} from "@/store";
import { twClassNames } from "@scripts/react/helpers/twClassNames";
import { MediaItem } from "@/types/media";
interface TimelineInferenceProgressOverlayProps {
    mediaItem: MediaItem;
}

export const TimelineInferenceProgressOverlay: React.FC<TimelineInferenceProgressOverlayProps> = ({
    mediaItem
}) => {
    const predictionsForFileAndFrameSegment = usePredictionsForFileAndFrameSegment();
    const inferenceQueueItem = useGetInferenceQueueItemById(mediaItem.id);

    // Use FPS from video metadata for consistency with stored frame_count
    const videoFps = mediaItem.metadata?.videoMetadata?.fps || 30;

    const totalFrames = useMemo(
        () => mediaItem.metadata?.frame_count || 0,
        [mediaItem.metadata?.frame_count]
    );
    const fileDuration = useMemo(
        () => mediaItem.metadata?.videoMetadata?.duration || 0,
        [mediaItem.metadata?.videoMetadata?.duration]
    );

    // Get unique frame segments that have predictions (to avoid overlapping overlays)
    const frameSegmentsWithPredictions = useMemo(() => {
        if (!mediaItem.id) return [];

        const filePredictions = predictionsForFileAndFrameSegment[mediaItem.id];
        if (!filePredictions) return [];

        return Object.entries(filePredictions)
            .filter(([, predictions]) => {
                // any entry that is not undefined counts as a prediction
                return predictions !== undefined;
            })
            .map(([frameSegment]) => parseInt(frameSegment, 10));
    }, [predictionsForFileAndFrameSegment, mediaItem.id]);

    // Group consecutive frame segments without predictions into ranges
    const frameRangesWithoutPredictions = useMemo(() => {
        if (!mediaItem.id || totalFrames === 0) return [];

        const frameSegmentsWithPredictionsSet = new Set(frameSegmentsWithPredictions);
        const ranges: Array<{ start: number; end: number }> = [];
        let currentRangeStart: number | null = null;

        // Iterate through all frame segments to find consecutive ranges
        for (let i = 0; i < totalFrames; i++) {
            const hasNoPrediction = !frameSegmentsWithPredictionsSet.has(i);

            if (hasNoPrediction) {
                // Start a new range if we're not in one
                if (currentRangeStart === null) {
                    currentRangeStart = i;
                }
            } else {
                // End the current range if we were in one
                if (currentRangeStart !== null) {
                    ranges.push({ start: currentRangeStart, end: i - 1 });
                    currentRangeStart = null;
                }
            }
        }

        // Close any open range at the end
        if (currentRangeStart !== null) {
            ranges.push({ start: currentRangeStart, end: totalFrames - 1 });
        }

        return ranges;
    }, [frameSegmentsWithPredictions, totalFrames, mediaItem.id]);

    // Only show the overlay if the inference is inferring or queued
    if (
        !inferenceQueueItem?.status ||
        ![InferenceStatus.INFERRING, InferenceStatus.QUEUED].includes(inferenceQueueItem.status)
    )
        return null;

    return (
        <>
            <style>{`
                @keyframes pulse-shadow {
                    0%, 100% {
                        box-shadow: 0 0 0 0 rgba(139, 92, 246, 0);
                    }
                    50% {
                        box-shadow: 0 0 20px 4px rgba(139, 92, 246, 0.6);
                    }
                }
            `}</style>
            {/* Opaque overlays for grouped frame ranges WITHOUT predictions - creates "reveal" effect */}
            {frameRangesWithoutPredictions.map((range, index) => {
                const startTimeInVideo = range.start / videoFps;
                const endTimeInVideo = (range.end + 1) / videoFps; // +1 to include the end frame
                const leftPercent = (startTimeInVideo / fileDuration) * 100;
                const rangeDuration = endTimeInVideo - startTimeInVideo;
                const widthPercent = (rangeDuration / fileDuration) * 100;

                return (
                    <div
                        key={`no-prediction-range-${range.start}-${range.end}`}
                        className={twClassNames(
                            "absolute z-20 flex h-full items-center justify-center rounded bg-gray-900/80 transition-all duration-300"
                        )}
                        style={{
                            left: `${Math.max(0, Math.min(leftPercent, 100))}%`,
                            width: `${Math.max(0.1, Math.min(widthPercent, 100 - leftPercent))}%`,
                            animation: "pulse-shadow 2s ease-in-out infinite"
                        }}
                    >
                        <i className="far fa-spinner fa-spin text-xs text-white"></i>
                    </div>
                );
            })}
        </>
    );
};
